import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

export const INSIGHTS_TABS = ['summary', 'transcript', 'ask'] as const;
export type InsightsTab = typeof INSIGHTS_TABS[number];
export type InsightsEntry = 'call_details' | 'contact_history' | 'legacy_link' | 'link';

export const PARAM_CALL = 'insights';
export const PARAM_TAB = 'tab';
export const PARAM_TIME = 't';

let pendingEntry: InsightsEntry = 'link';

export const takeInsightsEntry = (): InsightsEntry => {
    const entry = pendingEntry;
    pendingEntry = 'link';
    return entry;
};

export const markInsightsEntry = (entry: InsightsEntry): void => {
    pendingEntry = entry;
};

let returnFocusTarget: HTMLElement | null = null;

const focusedElement = (): HTMLElement | null => {
    if (typeof document === 'undefined') return null;
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body ? active : null;
};

export const peekInsightsReturnFocus = (): HTMLElement | null => returnFocusTarget;

export const clearInsightsReturnFocus = (): void => {
    returnFocusTarget = null;
};

export const parseCallId = (value: string | null): number | null =>
    value && /^\d{1,15}$/.test(value) ? Number(value) : null;

export const parseTab = (value: string | null): InsightsTab =>
    (INSIGHTS_TABS as readonly string[]).includes(value ?? '') ? (value as InsightsTab) : 'summary';

export const parseTime = (value: string | null): number | null => {
    if (!value || !/^\d{1,6}(\.\d{1,3})?$/.test(value)) return null;
    return Number(value);
};

export const insightsSearch = (callId: number, options: {tab?: InsightsTab; time?: number | null} = {}): string => {
    const params = new URLSearchParams({[PARAM_CALL]: String(callId)});
    if (options.tab && options.tab !== 'summary') params.set(PARAM_TAB, options.tab);
    if (options.time != null && options.time > 0) params.set(PARAM_TIME, String(Math.floor(options.time)));
    return `?${params.toString()}`;
};

export const insightsShareUrl = (callId: number, time: number): string =>
    `${window.location.origin}/calls${insightsSearch(callId, {tab: 'transcript', time})}`;

export interface InsightsDrawerParams {
    callId: number | null;
    tab: InsightsTab;
    time: number | null;
    open: (callId: number, options?: {tab?: InsightsTab; time?: number | null; entry?: InsightsEntry; returnFocus?: HTMLElement | null}) => void;
    close: () => void;
    setTab: (tab: InsightsTab) => void;
}

export const useInsightsDrawerParams = (): InsightsDrawerParams => {
    const [params, setParams] = useSearchParams();
    const callId = parseCallId(params.get(PARAM_CALL));
    const tab = parseTab(params.get(PARAM_TAB));
    const time = parseTime(params.get(PARAM_TIME));

    const open = useCallback<InsightsDrawerParams['open']>((id, options = {}) => {
        markInsightsEntry(options.entry ?? 'link');
        returnFocusTarget = options.returnFocus ?? focusedElement();
        setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set(PARAM_CALL, String(id));
            next.delete(PARAM_TAB);
            next.delete(PARAM_TIME);
            if (options.tab && options.tab !== 'summary') next.set(PARAM_TAB, options.tab);
            if (options.time != null && options.time > 0) next.set(PARAM_TIME, String(Math.floor(options.time)));
            return next;
        }, {replace: true});
    }, [setParams]);

    const close = useCallback(() => {
        setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.delete(PARAM_CALL);
            next.delete(PARAM_TAB);
            next.delete(PARAM_TIME);
            return next;
        }, {replace: true});
    }, [setParams]);

    const setTab = useCallback((nextTab: InsightsTab) => {
        setParams((previous) => {
            const next = new URLSearchParams(previous);
            if (nextTab === 'summary') next.delete(PARAM_TAB);
            else next.set(PARAM_TAB, nextTab);
            next.delete(PARAM_TIME);
            return next;
        }, {replace: true});
    }, [setParams]);

    return useMemo(() => ({callId, tab, time, open, close, setTab}), [callId, tab, time, open, close, setTab]);
};
